import { ChevronDown, Download } from 'lucide-react';
import React, { useState } from 'react';
import FilterIcon from '../../../../assets/icons/filter-icon.svg';
import { Button } from '../../../../components/button';
import { Card, CardContent } from '../../../../components/card';
import { DatePicker } from '../../../../components/date-picker';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../../../components/dropdown-menu';
import { Label } from '../../../../components/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../components/select';

export interface LiveEventFilters {
    eventName?: string;   // event id or 'all'
    location?: string;    // location id or 'all'
    demographics?: string;
    startDate?: string;
    endDate?: string;
    quickFilter?: string;
}

export interface LiveEventOption { id: string; name: string }
export interface LiveEventLocationOption { id: string; name: string }

const QUICK_FILTER_OPTIONS = [
    { value: 'last_7_days', label: 'Last 7 Days' },
    { value: 'last_30_days', label: 'Last 30 Days' },
    { value: 'last_90_days', label: 'Last 90 Days' },
    { value: 'this_month', label: 'This Month' },
    { value: 'last_month', label: 'Last Month' },
];

interface LiveEventFiltersSectionProps {
    filters: LiveEventFilters;
    onFiltersChange: (filters: LiveEventFilters) => void;
    onApplyFilters: (filters: LiveEventFilters) => void;
    /** Single handler (fallback) hoặc dùng onExportCSV / onExportXLSX / onExportPDF riêng */
    onExportData?: () => void;
    onExportCSV?: () => void;
    onExportXLSX?: () => void;
    onExportPDF?: () => void;
    /** Event options for dropdown (from API) */
    eventOptions?: LiveEventOption[];
    /** Location options for dropdown (from API) */
    locationOptions?: LiveEventLocationOption[];
}

export const LiveEventFiltersSection: React.FC<LiveEventFiltersSectionProps> = ({
    filters,
    onFiltersChange,
    onApplyFilters,
    onExportData,
    onExportCSV,
    onExportXLSX,
    onExportPDF,
    eventOptions = [],
    locationOptions = [],
}) => {
    const [localFilters, setLocalFilters] = useState<LiveEventFilters>(filters);

    const handleChange = (key: keyof LiveEventFilters, value: string | undefined) => {
        const updated = { ...localFilters, [key]: value };
        setLocalFilters(updated);
        onFiltersChange(updated);
    };

    const handleDateChange = (field: 'startDate' | 'endDate', date: Date | undefined) => {
        handleChange(field === 'startDate' ? 'startDate' : 'endDate', date ? date.toISOString().split('T')[0] : undefined);
    };

    return (
        <Card className="rounded-[12.13px] bg-white dark:bg-gray-800 border border-[#E6E6E6] dark:border-gray-700">
            <CardContent className="p-[1.6px]">
                {/* Header row */}
                <div className="flex items-start justify-end px-[19.50px] py-[15px]">
                    <div className="flex flex-1 gap-[6.50px] items-center">
                        <img src={FilterIcon} alt="Filter" className="w-[16.25px] h-[16.25px] shrink-0" />
                        <span
                            className="text-[#364153] dark:text-white text-[13px] font-semibold leading-[13px]"
                            style={{ fontFamily: 'Nunito' }}
                        >
                            Filters & Data Controls
                        </span>
                    </div>
                    <div className="flex flex-1 justify-end">
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button
                                    className="bg-[#00D6A8] hover:bg-[#00c49a] text-[#004232] text-[11.40px] font-bold leading-[16.25px] px-[15px] py-[6px] rounded-[6.13px] gap-[7px] h-auto"
                                    style={{ fontFamily: 'Nunito' }}
                                >
                                    <Download className="w-[13px] h-[13px]" />
                                    Export Data
                                    <ChevronDown className="w-[13px] h-[13px] opacity-50" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent className="w-48">
                                <DropdownMenuItem onClick={onExportCSV ?? onExportData}>Export as CSV</DropdownMenuItem>
                                <DropdownMenuItem onClick={onExportXLSX ?? onExportData}>Export as xlsx</DropdownMenuItem>
                                <DropdownMenuItem onClick={onExportPDF ?? onExportData}>Export as PDF</DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </div>

                {/* Filters row */}
                <div className="flex flex-wrap items-end justify-center gap-[30px] pb-[19.50px] px-[19.50px] w-full">
                        {/* Event Name */}
                        <div className="flex-1 min-w-[140px] flex flex-col gap-[6.50px] pt-[1.5px]">
                            <Label className="text-[11.40px] font-medium text-[#364153] dark:text-gray-300 leading-[16.25px]" style={{ fontFamily: 'Nunito' }}>
                                Event Name
                            </Label>
                            <Select
                                value={localFilters.eventName ?? 'all'}
                                onValueChange={(v) => handleChange('eventName', v === 'all' ? undefined : v)}
                            >
                                <SelectTrigger className="h-[29.25px] border border-[#B5BABE] dark:border-gray-600 rounded-[6.13px] px-[11.35px] py-[8.10px] text-[11.40px] font-normal bg-white dark:bg-gray-700 text-[#0A0A0A] dark:text-gray-200" style={{ fontFamily: 'Nunito' }}>
                                    <SelectValue placeholder="All Events" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All Events</SelectItem>
                                    {eventOptions.map((opt) => (
                                        <SelectItem key={opt.id} value={opt.id}>{opt.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Location */}
                        <div className="flex-1 min-w-[140px] flex flex-col gap-[6.50px]">
                            <Label className="text-[11.40px] font-medium text-[#364153] dark:text-gray-300 leading-[16.25px]" style={{ fontFamily: 'Nunito' }}>
                                Location
                            </Label>
                            <Select
                                value={localFilters.location ?? 'all'}
                                onValueChange={(v) => handleChange('location', v === 'all' ? undefined : v)}
                            >
                                <SelectTrigger className="h-[29.25px] border border-[#B5BABE] dark:border-gray-600 rounded-[6.13px] px-[11.35px] py-[8.10px] text-[11.40px] font-normal bg-white dark:bg-gray-700 text-[#0A0A0A] dark:text-gray-200" style={{ fontFamily: 'Nunito' }}>
                                    <SelectValue placeholder="All Locations" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All Locations</SelectItem>
                                    {locationOptions.map((opt) => (
                                        <SelectItem key={opt.id} value={opt.id}>{opt.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Demographics */}
                        <div className="flex-1 min-w-[140px] flex flex-col gap-[6.50px] pt-[1.5px]">
                            <Label className="text-[11.40px] font-medium text-[#364153] dark:text-gray-300 leading-[16.25px]" style={{ fontFamily: 'Nunito' }}>
                                Demographics
                            </Label>
                            <Select
                                value={localFilters.demographics ?? 'all'}
                                onValueChange={(v) => handleChange('demographics', v === 'all' ? undefined : v)}
                            >
                                <SelectTrigger className="h-[29.25px] border border-[#B5BABE] dark:border-gray-600 rounded-[6.13px] px-[11.35px] py-[8.10px] text-[11.40px] font-normal bg-white dark:bg-gray-700 text-[#0A0A0A] dark:text-gray-200" style={{ fontFamily: 'Nunito' }}>
                                    <SelectValue placeholder="All Users" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All Users</SelectItem>
                                    <SelectItem value="ages_18_34">Ages 18-34</SelectItem>
                                    <SelectItem value="ages_35_54">Ages 35-54</SelectItem>
                                    <SelectItem value="ages_55_plus">Ages 55+</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Date Range */}
                        <div className="w-[260px] flex flex-col gap-[6.50px]">
                            <Label className="text-[11.40px] font-medium text-[#364153] dark:text-gray-300 leading-[16.25px]" style={{ fontFamily: 'Nunito' }}>
                                Date Range
                            </Label>
                            <div className="flex gap-[6.50px] items-center">
                                <div className="flex-1 min-w-0" style={{ fontFamily: 'Nunito' }}>
                                    <DatePicker
                                        value={localFilters.startDate ? new Date(localFilters.startDate) : undefined}
                                        onChange={(date) => handleDateChange('startDate', date)}
                                        placeholder="Start date"
                                        dateFormat="MMM d"
                                        className="h-[29.25px] border border-[#B5BABE] dark:border-gray-600 rounded-[6.13px] px-[11.35px] py-[8.10px] text-[9.80px] font-medium bg-white dark:bg-gray-700 text-[#0A0A0A] dark:text-gray-200"
                                        placeholderIconColor="#00C49A"
                                        hideIcon={false}
                                    />
                                </div>
                                <span className="text-[13px] text-[#99A1AF] dark:text-gray-500 leading-[19.50px]" style={{ fontFamily: 'Nunito' }}>
                                    -
                                </span>
                                <div className="flex-1 min-w-0" style={{ fontFamily: 'Nunito' }}>
                                    <DatePicker
                                        value={localFilters.endDate ? new Date(localFilters.endDate) : undefined}
                                        onChange={(date) => handleDateChange('endDate', date)}
                                        placeholder="End date"
                                        dateFormat="MMM d"
                                        minDate={localFilters.startDate ? new Date(localFilters.startDate) : undefined}
                                        className="h-[29.25px] border border-[#B5BABE] dark:border-gray-600 rounded-[6.13px] px-[11.35px] py-[8.10px] text-[9.80px] font-medium bg-white dark:bg-gray-700 text-[#0A0A0A] dark:text-gray-200"
                                        placeholderIconColor="#00C49A"
                                        hideIcon={false}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Quick Filters */}
                        <div className="flex-1 min-w-[140px] flex flex-col gap-[6.50px]">
                            <Label className="text-[11.40px] font-medium text-[#364153] dark:text-gray-300 leading-[16.25px]" style={{ fontFamily: 'Nunito' }}>
                                Quick Filters
                            </Label>
                            <Select
                                value={localFilters.quickFilter ?? 'last_7_days'}
                                onValueChange={(v) => handleChange('quickFilter', v)}
                            >
                                <SelectTrigger className="h-[29.25px] border border-[#B5BABE] dark:border-gray-600 rounded-[6.13px] px-[11.35px] py-[8.10px] text-[11.40px] font-normal bg-white dark:bg-gray-700 text-[#0A0A0A] dark:text-gray-200" style={{ fontFamily: 'Nunito' }}>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {QUICK_FILTER_OPTIONS.map((opt) => (
                                        <SelectItem key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Apply Filters */}
                        <Button
                            onClick={() => onApplyFilters(localFilters)}
                            className="bg-[#00D6A8] hover:bg-[#00c49a] text-[#004232] text-[11.40px] font-bold leading-[16.25px] px-[15px] py-[6px] rounded-[6.13px] shrink-0 h-auto"
                            style={{ fontFamily: 'Nunito' }}
                        >
                            Apply Filters
                        </Button>
                </div>
            </CardContent>
        </Card>
    );
};
